import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate, useParams } from "react-router-dom";
import { useAuth } from 'react-oidc-context';

import { Helmet } from 'react-helmet-async';
import Grid from '@mui/material/Grid2';

import {  
  User, 
} from "@models";
import { 
  Campaign, 
  Character,
  CharacterOptions
} from "@dndModels";
import API from "@dndUtilities/system_api";
import DandDCharacterUtilities from "@dndUtilities/char_utilities";
import CommonUtilities from "@utilities/common_utilities";

import CenteredMenu from "@components/input/menus/CenteredMenu";
import ButtonBox from "@components/input/buttons/ButtonBox";
import LoadingBox from "@components/input/LoadingBox";

import ObjectDetails from "@dndComponents/ObjectDetails";
import CharacterOptionsInput from "@dndComponents/character/misc/CharacterOptionsInput";
import ToggleButtonBox from "@components/input/buttons/ToggleButtonBox";
import ToastBox from "@components/display/ToastBox";
import DisplayObject from "@dndComponents/display/DisplayObject";
import SendToCharacters from "@dndComponents/campaign/SendToCharacters";
import MailButton from "@components/input/buttons/MailButton";
import CharConnectUtilities from '@dndUtilities/char_connect_utilities';
import { Button } from '@mui/material';

const api = API.getInstance();
const char_util = DandDCharacterUtilities.getInstance();
const common_util = CommonUtilities.getInstance();
const connect_util = CharConnectUtilities.getInstance();

export default function CampaignDetails() {
  const auth = useAuth();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loading_message, setLoadingMessage] = useState("");
  const [_load_object, setLoadObject] = useState("");
  const [remove_characters_for_user_id, setRemoveCharactersForUserId] = useState("");
  const [percentage, setPercentage] = useState(0);
  const [fetched, setFetched] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [navigateTo, setNavigateTo] = useState<string | null>(null);
  const [obj, setObj] = useState<Campaign>(new Campaign());
  const [players, setPlayers] = useState<User[]>([]);
  const [characters, setCharacters] = useState<Character[]>([]);
  const [my_characters, setMyCharacters] = useState<Character[]>([]);
  const [add_existing, setAddExisting] = useState(false);
  const [tab, setTab] = useState("Characters");
  const [ping, setPing] = useState(false);
  const [toast_time, setToastTime] = useState(-1);
  const [open, setOpen] = useState(false);
  const [logging_in, setLoggingIn] = useState(false);
  const [name_for_helmet, setNameForHelmet] = useState(obj.name);
  const mounted = useRef(false);

  useEffect(() => { 
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  // Loads the editing Campaign into state
  const load_object = useCallback(async (id: string) => {
    // debugger;
    const res = await api.getObjects<Campaign>({ data_type: "campaign", filter: { "_id*ObjectIDs": [id] }});
    if (res.length === 1) {
      const new_obj = res[0];
      setObj(new_obj);
      // Get Users
      let filter: any = {
        "_id*ObjectIDs": [new_obj.owner_id, ...new_obj.player_ids]
      };
      const res2 = await api.getUsers(filter);
      setPlayers(res2 as User[]);
      // Get Characters
      filter = {};
      filter.campaign_id = new_obj._id;
      const res3 = await api.getObjectsOld("character", filter);
      obj.characters = res3 as Character[];
      setCharacters(obj.characters);
      setLoading(false);
    }
  }, []);

  const load = useCallback(() => {
    console.log("load()");
    if (id && id !== "" && obj._id !== id) {
      load_object(id);
    } else {
      setLoading(false);
    }
    setLoggingIn(false);
  }, [id, obj, load_object]);
  
  useEffect(() => {
    if (!fetched && api.user) {
      console.log(api.user);
      console.log(auth);
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        load();
      }, 1000);
    }
  });
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);

  const onReload = () => {
    if (id && id !== "" && obj._id !== id) {
      setLoading(true);
      // console.log("126");
      load_object(id);
    } 
  }

  const updateCharacter = (obj: Character, onSuccess: Function) => {
    const user = api.user;
    if (user) {
      char_util.getCharacter({ id: obj._id }).then(res => {
        if (res) {
          res.campaign_id = obj.campaign_id;
          api.updateCharacter(res, ["main"], true).then(res => {
            char_util.getCharacterFromCookie(obj._id).then(res => {
              const cookie_char = res;
              if (cookie_char !== null) {
                cookie_char.campaign_id = obj.campaign_id;
                api.updateCharacter(cookie_char, ["main"]).then(() => {
                  onSuccess();
                });
              } else {
                onSuccess();
              }
            });
          });
        }
      });
    }
  }

  const remove_characters_for_user = (user_id: string) => {
    const characters_for_user = characters.filter(o => o.owner_id === user_id);
    if (characters_for_user.length > 0) {
      const char = characters_for_user[0];
      char.campaign_id = "";
      char.active = true;
      updateCharacter(char, () => {
        const new_characters = characters.filter(o => o.campaign_id === obj._id);
        setCharacters(new_characters);
        setRemoveCharactersForUserId(user_id);
      });
    } else if (api.user && api.user._id === user_id) {
      setProcessing(false);
      setNavigateTo(`/campaign`);
    } else {
      setProcessing(false);
    }
  }

  // eslint-disable-next-line
  useEffect(() => {
    if (remove_characters_for_user_id !== "") {
      const id = remove_characters_for_user_id;
      setRemoveCharactersForUserId("");
      remove_characters_for_user(id);
    }
  });
  
  const renderExtras = () => {
    if (open) {
      return (
        <SendToCharacters
          campaign={obj}
          open
          onClose={() => { setOpen(false); }}
        />
      );
    }
    return null;
  }

  const copyCharacter = (obj: Character) => {
    // if (char_util.ready) {
    const user = api.user;
    if (user) {
      char_util.getCharacter({id: obj._id}).then(res => {
        if (res) {
          const new_obj = res.clone(true);
          new_obj.name = new_obj.name + " (Copy)";
          new_obj._id = "";
          new_obj.owner_id = user._id;
          api.createCharacter(new_obj).then(res => {
            if (res.body && !res.body.error) {
              setProcessing(false);
              setNavigateTo(`/character/${res.body.id}`);
            }
          });
        }
      });
    }
    // } else {
    //   setTimeout(() => {
    //     copyCharacter(obj);
    //   }, 500);
    // }
  }

  const renderCharacter = (char: Character, key: number) => {
    if (char.name.includes("Yanari")) {
      console.log(char);
    }
    if (api.user && (obj.owner_id === api.user._id || obj.player_ids.includes(api.user._id))) {
      if (char.owner_id === "") {
        return (
          <Grid
            key={key}
            container
            spacing={0}
            direction="row"
            size={{
              xs: 4
            }}>
            <Grid
              size={{
                xs: 12
              }}>
              <DisplayObject obj={char} />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <ButtonBox name="View" 
                disabled={ processing } 
                onClick={() => {
                  setNavigateTo(`/character/${char._id}`);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <ButtonBox name="Claim" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  if (api.user) {
                    setProcessing(true);
                    char.owner_id = api.user._id;
                    api.updateCharacter(char, ["main"], true).then(res => {
                      setProcessing(false);
                    });
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <ButtonBox name="Copy" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  if (api.user) {
                    setProcessing(true);
                    copyCharacter(char);
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <ButtonBox name={ char.active ? "Deactivate" : "Activate" }
                disabled={ processing || !api.online } 
                onClick={() => {
                  if (api.user) {
                    setProcessing(true);
                    char.active = !char.active;
                    char.is_archived = false;
                    api.updateCharacter(char, ["main"], true).then(res => {
                      setProcessing(false);
                    });
                  }
                }}
              />
            </Grid>
          </Grid>
        );
      } else if (api.user._id === char.owner_id || api.user._id === obj.owner_id) {
        const owner = players.find(o => o._id === char.owner_id);
        return (
          <Grid
            key={key}
            container
            spacing={0}
            direction="row"
            size={{
              xs: 4
            }}>
            <Grid
              size={{
                xs: 12
              }}>{ char.name }</Grid>
            <Grid
              className="small_label gray"
              size={{
                xs: 12
              }}>
              { char.race_and_class_string } { char.sidekick ? " (Sidekick)" : "" } - { owner?.profile_name??"" }
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <ButtonBox name="View" 
                disabled={ processing } 
                onClick={() => {
                  setNavigateTo(`/character/${char._id}`);
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 6
              }}>
              <ButtonBox name="Unassign" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  setProcessing(true);
                  char.owner_id = "";
                  api.updateCharacter(char, ["main"], true).then(res => {
                    setProcessing(false);
                  });
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <ButtonBox name="Copy" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  if (api.user) {
                    setProcessing(true);
                    copyCharacter(char);
                  }
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <ButtonBox 
                name="Leave Campaign" 
                disabled={ processing || !api.online } 
                onClick={() => {
                  setProcessing(true);
                  char.campaign_id = "";
                  char.active = true;
                  updateCharacter(char, () => {
                    const new_characters = characters.filter(o => o.campaign_id === obj._id);
                    setCharacters(new_characters);
                    setProcessing(false);
                  });
                }}
              />
            </Grid>
            <Grid
              size={{
                xs: 4
              }}>
              <ButtonBox name={ char.active ? "Deactivate" : "Activate" }
                disabled={ processing || !api.online } 
                onClick={() => {
                  if (api.user) {
                    setProcessing(true);
                    char.active = !char.active;
                    api.updateCharacter(char, ["main"], true).then(res => {
                      setProcessing(false);
                    });
                  }
                }}
              />
            </Grid>
          </Grid>
        );
      } else {
        const owner = players.find(o => o._id === char.owner_id);
        return (
          <Grid
            key={key}
            container
            spacing={0}
            direction="row"
            size={{
              xs: 4
            }}>
            <Grid
              size={{
                xs: 12
              }}>{ char.name }</Grid>
            { owner && 
              <Grid
                style={{ 
                    fontSize: "8px", 
                    color: "gray",
                  }}
                size={{
                  xs: 12
                }}>
                { char.race_and_class_string } { char.sidekick ? " (Sidekick)" : "" } - { owner.profile_name }
              </Grid>
            }
            <Grid
              size={{
                xs: 12
              }}>
              <ButtonBox name="View" 
                disabled={ processing } 
                onClick={() => {
                  setNavigateTo(`/character/${char._id}`);
                }}
              />
            </Grid>
          </Grid>
        );
      }
    } else {
      return (
        <Grid
          key={key}
          container
          spacing={0}
          direction="row"
          size={{
            xs: 4
          }}>
          <Grid
            size={{
              xs: 12
            }}>{ char.name }&nbsp;{ char.race_and_class_string }</Grid>
        </Grid>
      );
    }
  }

  const render_tab = () => {
    let return_me = <Grid>Coming Soon</Grid>;
    if (tab === "Options") {
      return_me = (
        <CharacterOptionsInput 
          obj={obj}
          disabled
          onChange={(options: CharacterOptions) => {
            
          }}
        />
      ); 
    } else if (tab === "Players") {
      return_me = (
        <Grid container spacing={0} direction="row">
          <Grid
            size={{
              xs: 12
            }}>
            <ButtonBox
              name="Copy Reusable Invite Link to Clipboard"
              onClick={() => {
                navigator.clipboard.writeText(`${window.location.origin}/campaign/join/${obj._id}/${obj.reusable_invite}`);
                setToastTime(common_util.get_time_number());
                setProcessing(false);
              }}
            />
            <ToastBox
              openTime={ toast_time }
            >
              Link copied to Clipboard
            </ToastBox>
          </Grid>
          { players.map((player, key) => {
            const player_characters = characters.filter(o => o.owner_id === player._id);
            return (
              <Grid
                key={key}
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  size={{
                    xs: 6
                  }}>
                  { player.use_name }&nbsp;
                  { player_characters.length > 0 && `(Characters: ${ player_characters.map(o => o.name).join(", ") })` }
                </Grid>
                { player._id === obj.owner_id ?
                  <Grid
                    size={{
                      xs: 6
                    }}>
                    Owner
                  </Grid>
                : api.user && obj.owner_id === api.user._id ?
                  <Grid
                    size={{
                      xs: 6
                    }}>
                    <ButtonBox 
                      name="Promote to Owner"
                      disabled={ processing || !api.online } 
                      onClick={() => {
                        if (api.user) {
                          setProcessing(true);
                          const new_obj = obj;
                          new_obj.player_ids = new_obj.player_ids.filter(o => o !== player._id);
                          new_obj.player_ids.push(api.user._id);
                          new_obj.owner_id = player._id;
                          api.updateObject("campaign", new_obj).then(res => {
                            setObj(new_obj);
                            setProcessing(false);
                          });
                        }
                      }}
                    />
                    <ButtonBox 
                      name="Remove Player"
                      disabled={ processing || !api.online } 
                      onClick={() => {
                        setProcessing(true);
                        const new_obj = obj;
                        new_obj.player_ids = new_obj.player_ids.filter(o => o !== player._id);
                        const new_players = players.filter(o => o._id !== player._id);
                        setPlayers(new_players);
                        api.updateObject("campaign", new_obj).then(res => {
                          remove_characters_for_user(player._id);
                        });
                      }}
                    />
                  </Grid> 
                : api.user && player._id === api.user._id &&
                  <Grid
                    size={{
                      xs: 6
                    }}>
                    <ButtonBox 
                      name="Leave Campaign"
                      // style={{
                      //   lineHeight: "1.1",
                      // }}
                      disabled={ processing || !api.online } 
                      onClick={() => {
                        setProcessing(true);
                        const new_obj = obj;
                        new_obj.player_ids = new_obj.player_ids.filter(o => o !== player._id);
                        const new_players = players.filter(o => o._id !== player._id);
                        setPlayers(new_players);
                        api.updateObject("campaign", new_obj).then(res => {
                          remove_characters_for_user(player._id);
                        });
                      }}
                    />
                  </Grid> 
                }
              </Grid>
            );
          })}
        </Grid>
      ); 
    } else if (tab === "Characters") {
      if (add_existing) {
        return_me = (
          <Grid container spacing={0} direction="column">
            <Grid>
              Adding an existing character which doesn't comply with the allowed options for this campaign will automatically undo those options on the character.<br/>
              This may include race, subrace, class, subclass, gifts, items, and spells.
            </Grid>
            <Grid container spacing={0} direction="row">
              { my_characters.map((char, key) => {
                return (
                  <Grid
                    key={key}
                    container
                    spacing={0}
                    direction="row"
                    size={{
                      xs: 4
                    }}>
                    <Grid
                      size={{
                        xs: 12
                      }}>{ char.name }</Grid>
                    { char.campaign_id === obj._id ? 
                      <Grid
                        size={{
                          xs: 12
                        }}>Already in Campaign</Grid>
                    : char.campaign_id === "" ? 
                      <Grid
                        size={{
                          xs: 12
                        }}>
                        <ButtonBox name="Join (can take a while)" 
                          disabled={ processing || !api.online } 
                          onClick={() => {
                            setProcessing(true);
                            char.campaign_id = obj._id;
                            connect_util.connectCampaign2(char, obj);
                            char_util.getCharacter({id: char._id}).then(res => {
                              if (res) {
                                const new_char = res;
                                new_char.campaign_id = new_char._id;
                                connect_util.connectCampaign2(new_char, obj);
                                new_char.last_campaign_update = 0;
                                api.updateCharacter(new_char, ["main"]).then(res => {
                                  api.updateCharacter(new_char, ["main"], true).then(res => {
                                    const new_characters = [...characters];
                                    new_characters.push(new_char);
                                    setAddExisting(false);
                                    setCharacters(new_characters);
                                    setProcessing(false);
                                  });
                                });
                              }
                            });
                          }}
                        />
                      </Grid>
                    : 
                      <Grid
                        size={{
                          xs: 12
                        }}>In a Different Campaign</Grid>
                    }
                  </Grid>
                );
              })}
            </Grid>
            <Grid>
              <ButtonBox name="Cancel" 
                disabled={ processing }
                onClick={() => {
                  setAddExisting(false);
                }}
              />
            </Grid>
          </Grid>
        );
      } else {
        return_me = (
          <Grid container spacing={0} direction="row">
            { api.user && 
              <Grid
                container
                spacing={0}
                direction="row"
                size={{
                  xs: 12
                }}>
                <Grid
                  size={{
                    xs: 4
                  }}>
                  <ButtonBox name="Add Existing Character" 
                    disabled={ processing || !api.online } 
                    onClick={() => {
                      if (my_characters.length > 0) {
                        setAddExisting(true);
                      } else if (api.user) {
                        setProcessing(true);
                        setAddExisting(true);
                        const filter: any = {};
                      
                        filter.owner_id = api.user._id;
                        api.getObjects({ data_type: "character", filter }).then(res => {
                          setMyCharacters(res as Character[]);
                          setProcessing(false);
                        });
                      }
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  <ButtonBox name="Create Character" 
                    disabled={ processing || !api.online } 
                    onClick={() => {
                      if (api.user) {
                        setProcessing(true);
                        const new_char = new Character();
                        new_char.owner_id = api.user._id;
                        new_char.campaign_id = new_char._id;
                        connect_util.connectCampaign2(new_char, obj);
                        new_char.last_campaign_update = obj.last_updated;
                        char_util.enforceOptions(new_char, obj).then(res => {
                          api.createObject("character", new_char).then(res => {
                            if (res && res.body.id) {
                              setProcessing(false);
                              setNavigateTo(`/character/${res.body.id}`);
                            } else {
                              console.log(res);
                            }
                          });
                        });
                      }
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  <ButtonBox name="Create Sidekick" 
                    disabled={ processing || !api.online } 
                    onClick={() => {
                      if (api.user) {
                        setProcessing(true);
                        const new_char = new Character();
                        new_char.owner_id = api.user._id;
                        new_char.campaign_id = new_char._id;
                        new_char.sidekick = true;
                        connect_util.connectCampaign2(new_char, obj);
                        new_char.last_campaign_update = obj.last_updated;
                        char_util.enforceOptions(new_char, obj).then(res => {
                          api.createObject("character", new_char).then(res => {
                            if (res && res.body.id) {
                              setProcessing(false);
                              setNavigateTo(`/character/${res.body.id}`);
                            } else {
                              console.log(res);
                            }
                          });
                        });
                      }
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  <ButtonBox name="Create Unassigned Character" 
                    disabled={ processing || !api.online } 
                    onClick={() => {
                      setProcessing(true);
                      const new_char = new Character();
                      new_char.owner_id = "";
                      new_char.campaign_id = new_char._id;
                      connect_util.connectCampaign2(new_char, obj);
                      new_char.last_campaign_update = obj.last_updated;
                      char_util.enforceOptions(new_char, obj).then(res => {
                        api.createObject("character", new_char).then(res => {
                          if (res && res.body.id) {
                            setProcessing(false);
                            setNavigateTo(`/character/${res.body.id}`);
                          } else {
                            console.log(res);
                          }
                        });
                      });
                    }}
                  />
                </Grid>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  <ButtonBox name="Create Unassigned Sidekick" 
                    disabled={ processing || !api.online } 
                    onClick={() => {
                      setProcessing(true);
                      const new_char = new Character();
                      new_char.sidekick = true;
                      new_char.owner_id = "";
                      new_char.campaign_id = new_char._id;
                      connect_util.connectCampaign2(new_char, obj);
                      new_char.last_campaign_update = obj.last_updated;
                      char_util.enforceOptions(new_char, obj).then(res => {
                        api.createObject("character", new_char).then(res => {
                          if (res && res.body.id) {
                            setProcessing(false);
                            setNavigateTo(`/character/${res.body.id}`);
                          } else {
                            console.log(res);
                          }
                        });
                      });
                    }}
                  />
                </Grid>
              </Grid>
            }
            { characters.filter(o => o.active).length > 0 &&
              <>
                <Grid
                  className='header centered'
                  size={{
                    xs: 12
                  }}>Active Characters</Grid>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  { characters.filter(o => o.active).map((char, key) => {
                    return renderCharacter(char, key);
                  })}
                </Grid>
              </>
            }
            { characters.filter(o => !o.active || o.is_archived).length > 0 &&
              <>
                <Grid
                  className='header centered'
                  size={{
                    xs: 12
                  }}>Inactive Characters</Grid>
                <Grid
                  container
                  spacing={0}
                  direction="row"
                  size={{
                    xs: 12
                  }}>
                  { characters.filter(o => !o.active || o.is_archived).map((char, key) => {
                    return renderCharacter(char, key);
                  })}
                </Grid>
              </>
            }
          </Grid>
        ); 
      }
    } else if (tab === "Recipes") {
      return_me = (
        <Grid container spacing={0} direction="row">
          { api.user && (obj.owner_id === api.user._id || api.user.admin) &&
            <>
              <Grid
                size={{
                  xs: 12
                }}>
                <ToggleButtonBox
                  name="Set Campaign Recipes"
                  value={ api.recipes_for_campaign !== null && api.recipes_for_campaign._id === obj._id }
                  onToggle={() => {
                    api.recipes_for_campaign = api.recipes_for_campaign !== null && api.recipes_for_campaign._id === obj._id ? null : obj;
                    setPing(!ping);
                  }}
                />
              </Grid> 
              <Grid
                size={{
                  xs: 12
                }}>
                If you select this option, you can then set recipes for items and magic item keywords to be used specifically for this campaign, on the pages for them.
              </Grid>
            </>
          }
          <Grid
            size={{
              xs: 12
            }}>
            Campaign Recipes
          </Grid>
        </Grid>
      )
    }
    return return_me;
  }

  console.log(api.user);
  if (navigateTo !== null) {
    return <Navigate to={navigateTo} />;
  } else if (logging_in) {
    return (
      <div className="centered">
        <LoadingBox message="Logging in" size={window.innerHeight * 0.5} height={window.innerHeight - 100} />
      </div>
    );
  } else if (api.user === null) {
    return (
      <div className="centered" style={{ height: "100%" }}>
        You must be logged in to view campaigns.
      </div>
    );
  } else if (!fetched || loading) {
    return (
      <div className="centered">
        <LoadingBox message={loading_message} percentage={percentage} size={window.innerHeight * 0.5} height={window.innerHeight - 100} />
      </div>
    );
  } else if (api.user._id !== obj.owner_id && !obj.player_ids.includes(api.user._id)) {
    return (
      <div>
        You are not part of this campaign.
      </div>
    );
  } else { 
    const formHeight = window.innerHeight - (window.innerWidth > 600 ? 228 : 228);
    const tabs = ["Characters","Players","Options","Recipes"];
    return (
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <ObjectDetails 
            obj={obj}
            extra_by_edit={() => {
              if (api.user && api.user._id === obj.owner_id) {
                return (
                  <>
                    <Button variant="contained" color="primary" style={{marginLeft: "8px"}}
                      onClick={ () => {
                        setNavigateTo(`/campaign/decks/${obj._id}`);
                      }}>
                      Decks for {obj.name}
                    </Button>
                    &nbsp;
                    <MailButton
                      onClick={() => {
                        setOpen(true);
                      }}
                    />
                  </>
                );
              }
              return null;
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <CenteredMenu
            options={tabs}
            selected={tab}
            onSelect={setTab}
          />
        </Grid>
        <Grid
          style={{ 
            height: `${formHeight}px`, 
            overflowY: "scroll", 
            overflowX: "hidden" 
          }}
          size={{
            xs: 12
          }}>
            { render_tab() }
        </Grid>
        { renderExtras() }
      </Grid>
    );
  }
}
